(() => {
  class Hotspots extends HTMLElement {
    /**
     * Initializes the Hotspots component.
     */
    constructor() {
      super();

      this.LABEL_GAP = 8; // Gap between two labels in pixels
      this.LINE_CLASS = "manual-section-hotspots__line";
      this.LABEL_CLASS = "manual-section-hotspots__label";
    }

    /**
     * Lifecycle method called when the element is added to the DOM.
     */
    connectedCallback() {
      this.renderHotspots();

      this.resizeObserver = new ResizeObserver(() => this.renderHotspots());
      this.resizeObserver.observe(this);
    }

    /**
     * Renders all hotspots by removing old elements and adding new labels and lines.
     */
    renderHotspots() {
      this.removeHotspotElements();
      this.hotSpots.forEach(hotspot => this.renderHotspot(hotspot));
    }

    /**
     * Removes all previously rendered hotspot labels and lines.
     */
    removeHotspotElements() {
      const elements = this.querySelectorAll("[data-hotspot-label], [data-hotspot-line]");

      elements.forEach(element => element.remove());
    }

    /**
     * Renders a single hotspot label and line.
     * @param {Element} hotspot - The hotspot element.
     */
    renderHotspot(hotspot) {
      const {
        startX, startY, endX, endY,
        labelText, labelLink, labelTwoText, labelTwoLink
      } = this.getHotspotData(hotspot);

      const label = this.createLabel(labelText, labelLink, startX, startY);
      const line = this.createLine(startX, startY, endX, endY);

      this.appendChild(label);
      this.appendChild(line);

      requestAnimationFrame(() => this.adjustLabelPosition(label));

      if (labelTwoText) this._createSecondLabel(labelTwoText, labelTwoLink, startX, startY, label);
    }

    /**
     * Adjusts the label position to ensure it stays within image bounds.
     * @param {Element} label - The label element to adjust.
     */
    adjustLabelPosition(label) {
      const imageRect = this.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      const gap = this.LABEL_GAP;
      const isOutOfBoundsLeft = labelRect.left < imageRect.left;
      const isOutOfBoundsRight = labelRect.right > imageRect.right;
      const isOutOfBoundsTop = labelRect.top < imageRect.top;
      const isOutOfBoundsBottom = labelRect.bottom > imageRect.bottom;

      let newLeft = parseFloat(label.style.left);
      let newTop = parseFloat(label.style.top);

      if (isOutOfBoundsLeft) newLeft += (imageRect.left + gap) - labelRect.left;
      if (isOutOfBoundsRight) newLeft -= labelRect.right - (imageRect.right - gap);
      if (isOutOfBoundsTop) newTop += (imageRect.top + gap) - labelRect.top;
      if (isOutOfBoundsBottom) newTop -= labelRect.bottom - (imageRect.bottom - gap);

      label.style.left = `${newLeft}px`;
      label.style.top = `${newTop}px`;
    }

    /**
     * Creates and appends a second label below the first one.
     * @param {string} text - The label text.
     * @param {string} link - The label link URL.
     * @param {number} left - The left position in pixels.
     * @param {number} top - The top position of the first label in pixels.
     * @param {Element} label - The first label element.
     * @private
     */
    _createSecondLabel(text, link, left, top, label) {
      const labelTwoTop = top + label.offsetHeight + this.LABEL_GAP;
      const labelTwo = this.createLabel(text, link, left, labelTwoTop);

      this.appendChild(labelTwo);

      // Wait for the label to render and measure widths
      requestAnimationFrame(() => this._adjustLabelPositions(label, labelTwo, left));
    }

    /**
     * Adjusts the horizontal position of the second label to align with the first.
     * @param {Element} label - The first label element.
     * @param {Element} labelTwo - The second label element.
     * @param {number} left - The original left position in pixels.
     * @private
     */
    _adjustLabelPositions(label, labelTwo, left) {
      const firstLeft = label.getBoundingClientRect().left;
      const secondLeft = labelTwo.getBoundingClientRect().left;
      const offset = firstLeft - secondLeft;

      labelTwo.style.left = (left + offset) + "px";
    }

    /**
     * Extracts and calculates hotspot data from the element.
     * @param {Element} hotspot - The hotspot element.
     * @returns {Object} Hotspot data including coordinates and label text.
     */
    getHotspotData(hotspot) {
      const imgWidth = this.offsetWidth;
      const imgHeight = this.offsetHeight;

      return {
        labelText: hotspot.dataset.label,
        labelLink: hotspot.dataset.link,
        labelTwoText: hotspot.dataset.labelTwo,
        labelTwoLink: hotspot.dataset.linkTwo,
        startX: parseFloat(hotspot.dataset.startX) / 100 * imgWidth,
        startY: parseFloat(hotspot.dataset.startY) / 100 * imgHeight,
        endX: parseFloat(hotspot.dataset.endX) / 100 * imgWidth,
        endY: parseFloat(hotspot.dataset.endY) / 100 * imgHeight
      };
    }

    /**
     * Creates a hotspot label element.
     * @param {string} text - The label text.
     * @param {string} link - The label link URL.
     * @param {number} left - The left position in pixels.
     * @param {number} top - The top position in pixels.
     * @returns {Element} The label element.
     */
    createLabel(text, link, left, top) {
      const label = document.createElement("div");

      label.className = this.LABEL_CLASS;
      label.dataset.hotspotLabel = "";
      label.style.left = left + "px";
      label.style.top = top + "px";

      if (link) this.createLink(label, link, text);
      else label.textContent = text;

      return label;
    }

    /**
     * Creates a link element and appends it to the label.
     * @param {Element} label - The label element.
     * @param {string} link - The link URL.
     * @param {string} text - The link text.
     */
    createLink(label, link, text) {
      const a = document.createElement("a");

      a.href = link;
      a.textContent = text;
      a.target = "_blank";

      label.appendChild(a);
    }

    /**
     * Creates a hotspot line element.
     * @param {number} startX - The starting X coordinate in pixels.
     * @param {number} startY - The starting Y coordinate in pixels.
     * @param {number} endX - The ending X coordinate in pixels.
     * @param {number} endY - The ending Y coordinate in pixels.
     * @returns {Element} The line element.
     */
    createLine(startX, startY, endX, endY) {
      const line = document.createElement("div");
      const dx = endX - startX;
      const dy = endY - startY;
      const length = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);

      line.className = this.LINE_CLASS;
      line.dataset.hotspotLine = "";
      line.style.left = startX + "px";
      line.style.top = startY + "px";
      line.style.width = length + "px";
      line.style.transform = `rotate(${angle}deg)`;

      return line;
    }

    /**
     * Returns all hotspot elements.
     * @returns {NodeListOf<Element>}
     */
    get hotSpots() {
      return this.querySelectorAll("[data-hotspot]");
    }
  }

  if (!customElements.get("hotspots-wrapper")) {
    customElements.define("hotspots-wrapper", Hotspots);
  }
})();